import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  Autocomplete,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  EventAvailable as EventAvailableIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  AlternateEmail as AlternateEmailIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import calendarApi, { Booking, BookingRecipient, BookingType } from './calendarApi';

interface Props {
  open: boolean;
  bookingId: string | null;
  initialStartAt?: string;
  initialEndAt?: string;
  bookingTypes: BookingType[];
  onClose: () => void;
  onSaved: (booking: Booking) => void;
  onDeleted: () => void;
}

interface UserOpt {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface AssetOpt {
  asset_id: string;
  asset_name?: string;
  serial_number?: string;
}

interface LocationOpt {
  location_id: string;
  location_name: string;
}

const STATUS_OPTIONS = ['confirmed', 'pending', 'completed', 'cancelled', 'no_show'] as const;

function formatStatus(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function userDisplayName(u: UserOpt) {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
  return name || u.email;
}

function userLabel(u: UserOpt) {
  const name = userDisplayName(u);
  return name === u.email ? u.email : `${name} (${u.email})`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        py: 2,
        borderTop: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 0, pt: 0 },
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
        {title}
      </Typography>
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}

const BookingDrawer: React.FC<Props> = ({
  open, bookingId, initialStartAt, initialEndAt, bookingTypes, onClose, onSaved, onDeleted,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [typeId, setTypeId] = useState<string>('');
  const [startAt, setStartAt] = useState<Dayjs | null>(null);
  const [endAt, setEndAt] = useState<Dayjs | null>(null);
  const [allDay, setAllDay] = useState(false);
  const [status, setStatus] = useState<typeof STATUS_OPTIONS[number]>('confirmed');
  const [locationText, setLocationText] = useState('');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<string | null>(null);

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerCompany, setCustomerCompany] = useState('');

  const [assignees, setAssignees] = useState<UserOpt[]>([]);
  const [recipientUsers, setRecipientUsers] = useState<UserOpt[]>([]);
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);

  const [linkedSafetyCheckId, setLinkedSafetyCheckId] = useState<string | null>(null);
  const [linkedLoanId, setLinkedLoanId] = useState<string | null>(null);
  const [linkedOutgoingDeliveryRequestId, setLinkedOutgoingDeliveryRequestId] = useState<string | null>(null);

  const [allUsers, setAllUsers] = useState<UserOpt[]>([]);
  const [assets, setAssets] = useState<AssetOpt[]>([]);
  const [locations, setLocations] = useState<LocationOpt[]>([]);

  const isEditing = !!bookingId;
  const isPublic = booking?.source === 'public';

  const resetForm = useCallback(() => {
    setBooking(null);
    setTitle('');
    setDescription('');
    setTypeId('');
    setStartAt(initialStartAt ? dayjs(initialStartAt) : dayjs().add(1, 'hour').startOf('hour'));
    setEndAt(initialEndAt ? dayjs(initialEndAt) : dayjs().add(2, 'hour').startOf('hour'));
    setAllDay(false);
    setStatus('confirmed');
    setLocationText('');
    setLocationId(null);
    setAssetId(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomerCompany('');
    setAssignees([]);
    setRecipientUsers([]);
    setRecipientEmails([]);
    setLinkedSafetyCheckId(null);
    setLinkedLoanId(null);
    setLinkedOutgoingDeliveryRequestId(null);
  }, [initialStartAt, initialEndAt]);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API_BASE_URL}/users`, { headers })
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.users || []);
        setAllUsers(arr.map((u: any) => ({
          user_id: u.user_id || u.id,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
        })));
      })
      .catch(() => setAllUsers([]));
    axios.get(`${API_BASE_URL}/assets`, { headers })
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.assets || []);
        setAssets(arr.slice(0, 500).map((a: any) => ({
          asset_id: a.asset_id || a.id,
          asset_name: a.asset_name || a.name,
          serial_number: a.serial_number,
        })));
      })
      .catch(() => setAssets([]));
    axios.get(`${API_BASE_URL}/locations`, { headers })
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.locations || []);
        setLocations(arr.map((l: any) => ({
          location_id: l.location_id || l.id,
          location_name: l.location_name || l.name,
        })));
      })
      .catch(() => setLocations([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!bookingId) {
      resetForm();
      return;
    }
    setLoading(true);
    calendarApi.getBooking(bookingId)
      .then((b) => {
        setBooking(b);
        setTitle(b.title);
        setDescription(b.description || '');
        setTypeId(b.typeId || '');
        setStartAt(dayjs(b.startAt));
        setEndAt(dayjs(b.endAt));
        setAllDay(!!b.allDay);
        setStatus(b.status as typeof STATUS_OPTIONS[number]);
        setLocationText(b.locationText || '');
        setLocationId(b.locationId);
        setAssetId(b.assetId);
        setCustomerName(b.customer.name || '');
        setCustomerEmail(b.customer.email || '');
        setCustomerPhone(b.customer.phone || '');
        setCustomerCompany(b.customer.company || '');
        setAssignees((b.assignees || []).map((a) => ({
          user_id: a.user_id,
          email: a.email || '',
          first_name: a.first_name,
          last_name: a.last_name,
        })));
        const ru: UserOpt[] = [];
        const re: string[] = [];
        for (const r of b.recipients || []) {
          if (r.user_id) {
            ru.push({
              user_id: r.user_id,
              email: r.user_email || '',
              first_name: r.first_name,
              last_name: r.last_name,
            });
          } else if (r.external_email) {
            re.push(r.external_email);
          }
        }
        setRecipientUsers(ru);
        setRecipientEmails(re);
        setLinkedSafetyCheckId(b.linkedSafetyCheckId);
        setLinkedLoanId(b.linkedLoanId);
        setLinkedOutgoingDeliveryRequestId(b.linkedOutgoingDeliveryRequestId);
      })
      .catch((e) => setError(e.message || 'Failed to load booking'))
      .finally(() => setLoading(false));
  }, [bookingId, open, resetForm]);

  useEffect(() => {
    if (isEditing || !typeId || !startAt) return;
    const t = bookingTypes.find((x) => x.type_id === typeId);
    if (!t) return;
    setEndAt(startAt.add(t.default_duration_minutes || 30, 'minute'));
    if (!title.trim()) setTitle(t.name);
  }, [typeId, startAt, isEditing, bookingTypes, title]);

  const handleSave = useCallback(async () => {
    if (!title.trim() || !startAt || !endAt) {
      setError('Please enter a title, start time, and end time.');
      return;
    }
    if (endAt.isBefore(startAt)) {
      setError('End time must be after the start time.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const recipients: Partial<BookingRecipient>[] = [
        ...recipientUsers.map((u) => ({ user_id: u.user_id, external_email: null })),
        ...recipientEmails.map((e) => ({ user_id: null, external_email: e })),
      ].map((r) => ({
        ...r,
        notify_create: 1,
        notify_update: 1,
        notify_cancel: 1,
        notify_reminder: 1,
      }));

      const payload: Record<string, unknown> = {
        title: title.trim(),
        description: description || null,
        bookingTypeId: typeId || null,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        allDay,
        status,
        locationText: locationText || null,
        locationId,
        assetId,
        customer: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone,
          company: customerCompany,
        },
        assignees: assignees.map((u) => ({ userId: u.user_id, role: 'attendee' })),
        recipients: recipients.map((r) => ({
          userId: r.user_id || null,
          externalEmail: r.external_email || null,
          notifyCreate: true,
          notifyUpdate: true,
          notifyCancel: true,
          notifyReminder: true,
        })),
        linkedSafetyCheckId,
        linkedLoanId,
        linkedOutgoingDeliveryRequestId,
      };

      const result = isEditing && booking
        ? await calendarApi.updateBooking(booking.bookingId, payload)
        : await calendarApi.createBooking(payload);

      onSaved(result);
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed to save booking');
    } finally {
      setSaving(false);
    }
  }, [
    title, description, typeId, startAt, endAt, allDay, status, locationText, locationId, assetId,
    customerName, customerEmail, customerPhone, customerCompany, assignees, recipientUsers, recipientEmails,
    linkedSafetyCheckId, linkedLoanId, linkedOutgoingDeliveryRequestId, isEditing, booking, onSaved, onClose,
  ]);

  const handleCancelBooking = useCallback(async () => {
    if (!booking) return;
    if (!window.confirm('Cancel this booking? Attendees will be notified.')) return;
    setSaving(true);
    try {
      await calendarApi.cancelBooking(booking.bookingId);
      onDeleted();
    } catch (e: any) {
      setError(e.message || 'Failed to cancel');
    } finally {
      setSaving(false);
    }
  }, [booking, onDeleted]);

  const handleComplete = useCallback(async (noShow: boolean) => {
    if (!booking) return;
    setSaving(true);
    try {
      await calendarApi.completeBooking(booking.bookingId, noShow);
      onSaved(booking);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to complete');
    } finally {
      setSaving(false);
    }
  }, [booking, onSaved, onClose]);

  const drawerHeading = useMemo(() => {
    if (loading) return 'Loading…';
    if (!isEditing) return 'New booking';
    return booking?.title || 'Edit booking';
  }, [loading, isEditing, booking]);

  const dateFieldProps = {
    size: 'small' as const,
    fullWidth: true,
    sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        disableScrollLock={false}
        ModalProps={{
          keepMounted: false,
          sx: { zIndex: (theme) => theme.zIndex.modal },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.42)',
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440, md: 480 },
            maxWidth: 'min(480px, 100vw)',
            top: 56,
            height: 'calc(100% - 56px)',
            borderLeft: '1px solid',
            borderColor: 'divider',
            boxShadow: '-8px 0 32px rgba(15, 23, 42, 0.14)',
            zIndex: (theme) => theme.zIndex.modal,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal,
          '& .MuiDrawer-paper': {
            position: 'fixed',
            right: 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'grey.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <EventAvailableIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                {drawerHeading}
              </Typography>
              {isPublic && (
                <Chip size="small" icon={<PublicIcon />} label="Public" color="info" variant="outlined" />
              )}
            </Stack>
            <IconButton onClick={onClose} edge="end" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          {loading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pb: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 0 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Section title="Details">
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  required
                  autoFocus={!isEditing}
                  placeholder="e.g. Demo with Acme Ltd"
                />

                <TextField
                  select
                  label="Booking type"
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) => {
                      if (!v) {
                        return (
                          <Typography component="span" variant="body2" color="text.secondary">
                            Select a type…
                          </Typography>
                        );
                      }
                      const t = bookingTypes.find((x) => x.type_id === v);
                      return t?.name || String(v);
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>No type</em>
                  </MenuItem>
                  {bookingTypes.map((t) => (
                    <MenuItem key={t.type_id} value={t.type_id}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t.default_duration_minutes} min
                            {t.is_public_bookable ? ' · Public' : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>

                {isEditing && (
                  <TextField
                    select
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as typeof STATUS_OPTIONS[number])}
                    fullWidth
                    SelectProps={{
                      renderValue: (v) => formatStatus(String(v)),
                    }}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {formatStatus(s)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <FormControlLabel
                  control={<Switch checked={allDay} onChange={(e) => setAllDay(e.target.checked)} size="small" />}
                  label="All-day event"
                />
              </Section>

              <Section title="When">
                <DateTimePicker
                  label="Starts"
                  value={startAt}
                  onChange={(v) => setStartAt(v)}
                  slotProps={{ textField: dateFieldProps }}
                />
                <DateTimePicker
                  label="Ends"
                  value={endAt}
                  onChange={(v) => setEndAt(v)}
                  minDateTime={startAt || undefined}
                  slotProps={{ textField: dateFieldProps }}
                />
              </Section>

              <Section title="Where">
                <Autocomplete
                  options={locations}
                  getOptionLabel={(o) => o.location_name}
                  value={locations.find((l) => l.location_id === locationId) || null}
                  onChange={(_, v) => setLocationId(v?.location_id || null)}
                  renderInput={(p) => <TextField {...p} label="Saved location" placeholder="Search locations…" />}
                  isOptionEqualToValue={(a, b) => a.location_id === b.location_id}
                />
                <TextField
                  label="Location details"
                  value={locationText}
                  onChange={(e) => setLocationText(e.target.value)}
                  placeholder="Address, room, video link, etc."
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Autocomplete
                  options={assets}
                  getOptionLabel={(o) => [o.asset_name, o.serial_number].filter(Boolean).join(' · ')}
                  value={assets.find((a) => a.asset_id === assetId) || null}
                  onChange={(_, v) => setAssetId(v?.asset_id || null)}
                  renderInput={(p) => <TextField {...p} label="Asset (optional)" placeholder="For demos or loans" />}
                  isOptionEqualToValue={(a, b) => a.asset_id === b.asset_id}
                />
                <TextField
                  label="Internal notes"
                  multiline
                  minRows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Anything the team should know before this booking"
                  fullWidth
                />
              </Section>

              <Section title="Customer">
                <TextField
                  label="Name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Company"
                  value={customerCompany}
                  onChange={(e) => setCustomerCompany(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Phone"
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  fullWidth
                />
              </Section>

              <Section title="Team">
                <Autocomplete
                  multiple
                  options={allUsers}
                  getOptionLabel={userLabel}
                  value={assignees}
                  onChange={(_, v) => setAssignees(v as UserOpt[])}
                  renderInput={(p) => (
                    <TextField {...p} label="Assigned staff" placeholder="Who is handling this?" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((u, i) => (
                      <Chip
                        icon={<PersonIcon />}
                        key={u.user_id}
                        label={userDisplayName(u)}
                        size="small"
                        {...getTagProps({ index: i })}
                      />
                    ))
                  }
                  isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
                />
                <Autocomplete
                  multiple
                  options={allUsers}
                  getOptionLabel={userLabel}
                  value={recipientUsers}
                  onChange={(_, v) => setRecipientUsers(v as UserOpt[])}
                  renderInput={(p) => (
                    <TextField {...p} label="Also notify (staff)" placeholder="Additional team members" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((u, i) => (
                      <Chip
                        key={u.user_id}
                        label={userDisplayName(u)}
                        size="small"
                        {...getTagProps({ index: i })}
                      />
                    ))
                  }
                  isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[] as string[]}
                  value={recipientEmails}
                  onChange={(_, v) => setRecipientEmails(v as string[])}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      label="Also notify (external)"
                      placeholder="name@company.com"
                      helperText="Press Enter after each email"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((e, i) => (
                      <Chip
                        icon={<AlternateEmailIcon />}
                        key={e}
                        label={e}
                        size="small"
                        {...getTagProps({ index: i })}
                      />
                    ))
                  }
                />
              </Section>

              {isEditing && (
                <Section title="Linked records">
                  <TextField
                    label="Safety check ID"
                    value={linkedSafetyCheckId || ''}
                    onChange={(e) => setLinkedSafetyCheckId(e.target.value || null)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Loan ID"
                    value={linkedLoanId || ''}
                    onChange={(e) => setLinkedLoanId(e.target.value || null)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Outgoing delivery request ID"
                    value={linkedOutgoingDeliveryRequestId || ''}
                    onChange={(e) => setLinkedOutgoingDeliveryRequestId(e.target.value || null)}
                    fullWidth
                    size="small"
                  />
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {linkedSafetyCheckId && (
                      <MuiLink
                        href={`/admin/safety-checks?tab=responses&safety_check_id=${encodeURIComponent(linkedSafetyCheckId)}`}
                        variant="body2"
                      >
                        Open safety check
                      </MuiLink>
                    )}
                    {linkedLoanId && (
                      <MuiLink href="/admin/loan-checks?tab=responses" variant="body2">
                        Open loan
                      </MuiLink>
                    )}
                  </Stack>
                </Section>
              )}
            </Box>
          )}

          {/* Footer */}
          <Box
            sx={{
              flexShrink: 0,
              px: 2.5,
              py: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {isEditing && booking && booking.status !== 'cancelled' && (
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleCancelBooking}
                  disabled={saving}
                >
                  Cancel booking
                </Button>
              )}
              {isEditing && booking && (booking.status === 'confirmed' || booking.status === 'pending') && (
                <>
                  <Button variant="outlined" size="small" onClick={() => handleComplete(false)} disabled={saving}>
                    Complete
                  </Button>
                  <Button variant="outlined" size="small" color="warning" onClick={() => handleComplete(true)} disabled={saving}>
                    No-show
                  </Button>
                </>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose} disabled={saving}>
                Close
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
                {saving ? <CircularProgress size={20} color="inherit" /> : isEditing ? 'Save changes' : 'Create booking'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
};

export default BookingDrawer;
